// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { AddURLLinkParams, urlLinkSchema } from '@/url-link/api/types';
import { z } from 'zod';

// Response schema for the asset URL link creation endpoint
const addAssetURLLinkResponseSchema = z
  .object({
    data: urlLinkSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, ...data }));

type AddAssetURLLinkResponse = z.infer<typeof addAssetURLLinkResponseSchema>;

/**
 * Adds a URL link and associates it with a specific asset.
 * This function calls the backend endpoint POST /v1/asset/{assetKey}/url-link
 * with the URL link details in the request body.
 *
 * @param params - The parameters for creating the asset URL link
 * @returns Promise containing the created URL link details
 */
export async function addAssetURLLink(
  params: AddURLLinkParams
): Promise<AddAssetURLLinkResponse> {
  const apiUrl = `/v1/asset/${params.entityKey}/url-link`;
  const payload = {
    name: params.name,
    url: params.url,
  };

  const response = await api.post(apiUrl, payload);

  return parseResponse(addAssetURLLinkResponseSchema, response.data, {
    schemaName: 'AddAssetURLLink',
    endpoint: apiUrl,
  });
}
